import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Screen from '../../components/common/Screen';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import { buildCsv, downloadCsv, parseContactsCsv } from '../../csv';

/* The client's side of a project's mail plan (/event-communication/<eventId>).

   We write the mails in Machina; the client's admin reads each one here as their colleagues will
   get it, changes the text if they like, talks to us in a comment thread and approves. Nothing is
   sent from this screen and nothing is sent before it is approved — shared_models
   services/mail_plan holds every rule, the routes in api/routes/mail_plan.py only check who asks.

   ★ Times arrive as EVENT-LOCAL wall clock (ISO without an offset) and are printed literally:
   through Date() a 09:00 Dubai mail would read 07:00 in Brussels.
   ★ The text editor is a contentEditable box with the merge fields left visible as {{first_name}}:
   the server cleans whatever comes back (mail_plan.clean_body) before it is stored. */

const STATUS_CLASS = {
  draft: 'bg-light text-dark', in_review: 'bg-info text-dark', approved: 'bg-success',
  sending: 'bg-warning text-dark', sent: 'bg-primary', failed: 'bg-danger',
};

// "2026-05-25T09:00:00" → "Mon 25 May, 09:00" in the reader's language, read literally.
const wallClock = (iso, lang) => {
  if (!iso) return '';
  const [d, tm] = String(iso).split('T');
  const [y, m, day] = d.split('-').map(Number);
  const date = new Date(y, m - 1, day);
  const dayText = date.toLocaleDateString(lang || undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  return `${dayText}, ${(tm || '').slice(0, 5)}`;
};

const refusal = (t, err) => {
  const detail = err && err.data && err.data.detail;
  return detail || t(err?.message || 'saveFailed', { defaultValue: t('saveFailed') });
};

const BodyEditor = ({ initial, tokens, onChange }) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.innerHTML = initial || '';
  }, [initial]);
  const exec = (cmd, value) => {
    ref.current && ref.current.focus();
    document.execCommand(cmd, false, value);
    onChange(ref.current.innerHTML);
  };
  const insert = (key) => exec('insertText', `{{${key}}}`);
  const link = () => {
    const url = window.prompt(t('mailPlanLinkPrompt'), 'https://');
    if (url) exec('createLink', url);
  };
  return (
    <div>
      <div className="d-flex flex-wrap gap-1 mb-1">
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => exec('bold')}><b>B</b></button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => exec('italic')}><i>I</i></button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={link}>{t('mailPlanLink')}</button>
      </div>
      <div className="d-flex flex-wrap gap-1 mb-1">
        {tokens.map((tok) => (
          <button key={tok.key} type="button" className="btn btn-sm btn-light border py-0 px-2 small"
                  title={`{{${tok.key}}}`} onClick={() => insert(tok.key)}>{tok.label}</button>
        ))}
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning
           className="form-control text-start" style={{ minHeight: 260, overflowY: 'auto' }}
           onInput={() => onChange(ref.current.innerHTML)} />
    </div>
  );
};

const MailDetail = ({ eventId, itemId, onChanged }) => {
  const { t } = useTranslation();
  const [item, setItem] = useState(null);
  const [mode, setMode] = useState('preview');
  const [draft, setDraft] = useState(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', `/mail-plan/${eventId}/${itemId}`)
      .then((d) => { setItem(d); setDraft({ subject: d.subject, preheader: d.preheader, body: d.body }); })
      .catch((e) => setNotice({ kind: 'danger', text: refusal(t, e) }));
  }, [eventId, itemId, t]);
  useEffect(() => { setMode('preview'); setNotice(null); load(); }, [load]);

  const act = (method, path, body, after) => {
    setBusy(true);
    setNotice(null);
    apiRequest(method, path, body)
      .then((d) => { setItem(d); setDraft({ subject: d.subject, preheader: d.preheader, body: d.body }); if (after) after(d); onChanged(); })
      .catch((e) => setNotice({ kind: 'danger', text: refusal(t, e) }))
      .finally(() => setBusy(false));
  };

  if (!item) return <div className="small text-muted p-3">{notice ? notice.text : t('loading')}</div>;
  const canApprove = item.status === 'in_review';

  return (
    <div className="border rounded p-3 text-start bg-white">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <h5 className="mb-0 me-auto">{item.title}</h5>
        <span className={`badge ${STATUS_CLASS[item.status] || 'bg-light text-dark'}`}>{t(`mailPlanStatus_${item.status}`)}</span>
      </div>
      <div className="small text-muted mb-2">
        {wallClock(item.when)} · {item.list ? t('mailPlanTo', { list: item.list, n: item.preview.count }) : t('mailPlanNoList')}
      </div>
      {notice && <div className={`alert alert-${notice.kind} py-2 small`}>{notice.text}</div>}

      <div className="btn-group btn-group-sm mb-2">
        <button type="button" className={`btn btn-outline-secondary ${mode === 'preview' ? 'active' : ''}`} onClick={() => setMode('preview')}>{t('mailPlanPreview')}</button>
        {item.editable && (
          <button type="button" className={`btn btn-outline-secondary ${mode === 'edit' ? 'active' : ''}`} onClick={() => setMode('edit')}>{t('mailPlanEdit')}</button>
        )}
      </div>

      {mode === 'preview' ? (
        <>
          <div className="small mb-1"><strong>{item.preview.subject}</strong>
            {item.preview.preheader && <span className="text-muted"> — {item.preview.preheader}</span>}</div>
          {item.preview.as && <div className="small text-muted mb-1">{t('mailPlanPreviewAs', { name: item.preview.as })}</div>}
          <iframe title={item.title} sandbox="" className="w-100 border rounded" style={{ height: 420, background: '#fff' }}
                  srcDoc={`<div style="font-family:Arial,sans-serif;font-size:14px;padding:12px">${item.preview.body}</div>`} />
        </>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); act('PUT', `/mail-plan/${eventId}/${itemId}`, draft, (d) => { setMode('preview'); setNotice({ kind: d.back_to_review ? 'warning' : 'success', text: t(d.back_to_review ? 'mailPlanBackToReview' : 'mailPlanSaved') }); }); }}>
          <label className="form-label small mb-0" htmlFor="mp-subject">{t('mailPlanSubject')}</label>
          <input id="mp-subject" className="form-control form-control-sm mb-2" maxLength={300}
                 value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
          <label className="form-label small mb-0" htmlFor="mp-preheader">{t('mailPlanPreheader')}</label>
          <input id="mp-preheader" className="form-control form-control-sm mb-2" maxLength={300}
                 value={draft.preheader} onChange={(e) => setDraft({ ...draft, preheader: e.target.value })} />
          <BodyEditor initial={item.body} tokens={item.tokens} onChange={(html) => setDraft((d) => ({ ...d, body: html }))} />
          <div className="small text-muted mt-1">{t('mailPlanEditHint')}</div>
          <button type="submit" className="btn btn-sm btn-primary mt-2" disabled={busy}>{t('save')}</button>
        </form>
      )}

      {canApprove && (
        <button type="button" className="btn btn-success w-100 mt-3" disabled={busy}
                onClick={() => act('POST', `/mail-plan/${eventId}/${itemId}/approve`, {}, () => setNotice({ kind: 'success', text: t('mailPlanApprovedNotice') }))}>
          <Icon name="check" /> {t('mailPlanApprove')}
        </button>
      )}

      <h6 className="mt-4">{t('mailPlanComments')}</h6>
      {item.comments.length === 0 && <div className="small text-muted mb-2">{t('mailPlanNoComments')}</div>}
      {item.comments.map((c) => (
        <div key={c.id} className={`border-start border-3 ps-2 mb-2 ${c.side === 'us' ? 'border-warning' : 'border-primary'} ${c.resolved ? 'opacity-50' : ''}`}>
          <div className="small text-muted">{c.side === 'us' ? t('mailPlanFromUs') : (c.author || t('mailPlanFromYou'))} · {(c.at || '').slice(0, 16).replace('T', ' ')}</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{c.text}</div>
        </div>
      ))}
      <form onSubmit={(e) => { e.preventDefault(); if (comment.trim()) act('POST', `/mail-plan/${eventId}/${itemId}/comment`, { text: comment }, () => setComment('')); }}>
        <textarea className="form-control form-control-sm" rows={2} maxLength={2000} value={comment}
                  placeholder={t('mailPlanCommentPlaceholder')} onChange={(e) => setComment(e.target.value)} />
        <button type="submit" className="btn btn-sm btn-outline-secondary mt-2" disabled={busy || !comment.trim()}>{t('mailPlanSendComment')}</button>
      </form>
    </div>
  );
};

const frame = (html) => `<div style="font-family:Arial,sans-serif;font-size:14px;padding:12px">${html || ''}</div>`;

const CommentThread = ({ comments, onSend }) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const send = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    Promise.resolve(onSend(text)).then(() => setText('')).finally(() => setBusy(false));
  };
  return (
    <>
      <h6 className="mt-3">{t('mailPlanComments')}</h6>
      {comments.length === 0 && <div className="small text-muted mb-2">{t('mailPlanNoComments')}</div>}
      {comments.map((c) => (
        <div key={c.id} className={`border-start border-3 ps-2 mb-2 ${c.side === 'us' ? 'border-warning' : 'border-primary'} ${c.resolved ? 'opacity-50' : ''}`}>
          <div className="small text-muted">{c.side === 'us' ? t('mailPlanFromUs') : (c.author || t('mailPlanFromYou'))} · {(c.at || '').slice(0, 16).replace('T', ' ')}</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{c.text}</div>
        </div>
      ))}
      <form onSubmit={send}>
        <textarea className="form-control form-control-sm" rows={2} maxLength={2000} value={text}
                  placeholder={t('mailPlanCommentPlaceholder')} onChange={(e) => setText(e.target.value)} />
        <button type="submit" className="btn btn-sm btn-outline-secondary mt-2" disabled={busy || !text.trim()}>{t('mailPlanSendComment')}</button>
      </form>
    </>
  );
};

const FaqForm = ({ initial, onSave, onCancel, busy }) => {
  const { t } = useTranslation();
  const [question, setQuestion] = useState(initial.question || '');
  const [answer, setAnswer] = useState(initial.answer || '');
  return (
    <form className="border rounded p-2 mb-2 bg-light" onSubmit={(e) => { e.preventDefault(); onSave({ question, answer }); }}>
      <label className="form-label small mb-0" htmlFor="faq-q">{t('mailPlanFaqQuestion')}</label>
      <input id="faq-q" className="form-control form-control-sm mb-2" maxLength={500} required
             value={question} onChange={(e) => setQuestion(e.target.value)} />
      <div className="form-label small mb-0">{t('mailPlanFaqAnswer')}</div>
      <BodyEditor initial={initial.answer || ''} tokens={[]} onChange={setAnswer} />
      <div className="d-flex gap-2 mt-2">
        <button type="submit" className="btn btn-sm btn-primary" disabled={busy || !question.trim()}>{t('save')}</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel}>{t('cancel')}</button>
      </div>
    </form>
  );
};

/* The project's FAQ — theirs to change: each change is live at once and rings our side
   (services/faq_standard). Written in the event's language, one project, one language. Our own
   Quill HTML and their cleaned text, rendered as the players' /faq renders it. */
const FaqEditor = ({ eventId, faq, lang, onChange }) => {
  const { t, i18n } = useTranslation();
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  let langName = (lang || 'en').toUpperCase();
  try { langName = new Intl.DisplayNames([i18n.language || 'en'], { type: 'language' }).of(lang || 'en'); } catch (e) { /* old browser */ }
  const act = (method, path, body, done) => {
    setBusy(true);
    setNotice(null);
    apiRequest(method, path, body)
      .then((r) => { onChange(r); setEditing(null); if (done) setNotice({ kind: 'success', text: t(done) }); })
      .catch((e) => setNotice({ kind: 'danger', text: refusal(t, e) }))
      .finally(() => setBusy(false));
  };
  const save = (id) => (draft) => act(id ? 'PUT' : 'POST', `/mail-plan/${eventId}/faq${id ? `/${id}` : ''}`, draft, 'mailPlanFaqSaved');
  const remove = (f) => {
    if (window.confirm(t('mailPlanFaqRemoveConfirm', { question: f.question }))) act('DELETE', `/mail-plan/${eventId}/faq/${f.id}`);
  };
  const move = (f, step) => act('POST', `/mail-plan/${eventId}/faq/${f.id}/move`, { step });
  const own = faq.filter((f) => f.own);
  return (
    <div>
      <div className="small text-muted mb-2">{t('mailPlanFaqHint')} {t('mailPlanFaqLanguage', { lang: langName })}</div>
      {notice && <div className={`alert alert-${notice.kind} py-2 small`}>{notice.text}</div>}
      {faq.length === 0 && <div className="small text-muted mb-2">{t('mailPlanFaqEmpty')}</div>}
      <div className="list-group mb-2">
        {faq.map((f) => (editing === f.id ? (
          <FaqForm key={f.id} initial={f} busy={busy} onSave={save(f.id)} onCancel={() => setEditing(null)} />
        ) : (
          <div key={f.id} className="list-group-item">
            <div className="d-flex align-items-start gap-2">
              <strong className="me-auto">{f.question}</strong>
              {f.own && (
                <>
                  <button type="button" className="btn btn-sm btn-light border py-0" title={t('mailPlanFaqUp')} aria-label={t('mailPlanFaqUp')}
                          disabled={busy || own[0] === f} onClick={() => move(f, -1)}>↑</button>
                  <button type="button" className="btn btn-sm btn-light border py-0" title={t('mailPlanFaqDown')} aria-label={t('mailPlanFaqDown')}
                          disabled={busy || own[own.length - 1] === f} onClick={() => move(f, 1)}>↓</button>
                </>
              )}
              <button type="button" className="btn btn-sm btn-outline-secondary py-0" disabled={busy} onClick={() => setEditing(f.id)}>{t('mailPlanEdit')}</button>
              <button type="button" className="btn btn-sm btn-outline-danger py-0" disabled={busy} onClick={() => remove(f)}>{t('mailPlanFaqRemove')}</button>
            </div>
            <div className="small mt-1" dangerouslySetInnerHTML={{ __html: f.answer }} />
          </div>
        )))}
      </div>
      {editing === 'new'
        ? <FaqForm initial={{}} busy={busy} onSave={save(null)} onCancel={() => setEditing(null)} />
        : <button type="button" className="btn btn-sm btn-outline-primary" disabled={busy} onClick={() => setEditing('new')}><Icon name="plus" /> {t('mailPlanFaqAdd')}</button>}
    </div>
  );
};

/* The final instructions and the FAQ their colleagues will read, with a thread each so a change
   is asked for where the text is. The final instructions are ours, shown in a sandboxed frame
   like the mail preview; the FAQ they change themselves (FaqEditor). */
const ContentCard = ({ eventId }) => {
  const { t } = useTranslation();
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState('final_instructions');
  const [error, setError] = useState(null);
  useEffect(() => {
    apiRequest('GET', `/mail-plan/${eventId}/content`)
      .then(setContent).catch((e) => setError(refusal(t, e)));
  }, [eventId, t]);
  if (!content) return error ? <div className="small text-danger mb-3">{error}</div> : null;
  const send = (text) => apiRequest('POST', `/mail-plan/${eventId}/content/${tab}/comment`, { text })
    .then((r) => setContent({ ...content, comments: { ...content.comments, [tab]: r.comments } }))
    .catch((e) => setError(refusal(t, e)));
  return (
    <div className="border rounded p-3 mb-3 text-start bg-white">
      <h6>{t('mailPlanContentTitle')}</h6>
      <div className="btn-group btn-group-sm mb-2">
        <button type="button" className={`btn btn-outline-secondary ${tab === 'final_instructions' ? 'active' : ''}`} onClick={() => setTab('final_instructions')}>{t('mailPlanFinalInstructions')}</button>
        <button type="button" className={`btn btn-outline-secondary ${tab === 'faq' ? 'active' : ''}`} onClick={() => setTab('faq')}>{t('mailPlanFaq')}</button>
      </div>
      {tab === 'faq'
        ? <FaqEditor eventId={eventId} faq={content.faq} lang={content.faq_lang}
                     onChange={(r) => setContent((c) => ({ ...c, faq: r.faq, faq_lang: r.faq_lang }))} />
        : content.final_instructions
          ? <iframe title={tab} sandbox="" className="w-100 border rounded" style={{ height: 360, background: '#fff' }} srcDoc={frame(content.final_instructions)} />
          : <div className="small text-muted">{t('mailPlanInstructionsEmpty')}</div>}
      {error && <div className="small text-danger mt-2">{error}</div>}
      <CommentThread comments={content.comments[tab] || []} onSend={send} />
    </div>
  );
};

/* The automatic mails that are switched ON for the project, read-only: they leave by themselves
   when something happens (a password reset, an answer to the invitation, the event moved). What
   we switched off in Machina is never sent and is not listed. Loaded only when opened — it reads
   every mail text of the project. A change is asked for in the thread, like the FAQ. */
const AutomaticMailsCard = ({ eventId }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [shown, setShown] = useState(null);
  const [comments, setComments] = useState([]);
  const [error, setError] = useState(null);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !data) {
      apiRequest('GET', `/mail-plan/${eventId}/automatic`).then(setData).catch((e) => setError(refusal(t, e)));
      apiRequest('GET', `/mail-plan/${eventId}/content`)
        .then((c) => setComments((c.comments && c.comments.automatic) || [])).catch(() => {});
    }
  };
  const send = (text) => apiRequest('POST', `/mail-plan/${eventId}/content/automatic/comment`, { text })
    .then((r) => setComments(r.comments || []))
    .catch((e) => setError(refusal(t, e)));
  const when = (name) => (i18n.exists(`autoMail_${name}`) ? t(`autoMail_${name}`) : null);
  return (
    <div className="border rounded p-3 mb-3 text-start bg-white">
      <button type="button" className="btn btn-link p-0 text-decoration-none text-reset d-flex w-100 align-items-center" onClick={toggle}>
        <h6 className="mb-0 me-auto">{t('autoMailsTitle')}</h6>
        <span className="small text-muted">{open ? '▲' : t('autoMailsShow')}</span>
      </button>
      {open && (
        <>
          <div className="small text-muted my-2">{t('autoMailsHint')}</div>
          {!data && !error && <div className="small text-muted">{t('loading')}</div>}
          {data && data.mails.length === 0 && <div className="small text-muted">{t('autoMailsEmpty')}</div>}
          {data && data.mails.length > 0 && (
            <div className="list-group mb-2">
              {data.mails.map((m) => (
                <React.Fragment key={m.name}>
                  <button type="button" className="list-group-item list-group-item-action"
                          onClick={() => setShown(shown === m.name ? null : m.name)}>
                    <strong className="d-block">{when(m.name) || m.subject}</strong>
                    {when(m.name) && <span className="small text-muted">{m.subject}</span>}
                  </button>
                  {shown === m.name && (
                    <div className="list-group-item p-2 bg-light">
                      <iframe title={m.name} sandbox="" className="w-100 border rounded" style={{ height: 360, background: '#fff' }}
                              srcDoc={frame(m.body)} />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
          {error && <div className="small text-danger mt-2">{error}</div>}
          <CommentThread comments={comments} onSend={send} />
        </>
      )}
    </div>
  );
};

/* Visuals for their own comms — items of our CRM media library picked for this project or in the
   brand's standard kit, plus the event's banner and logo. Each link is the item's counted
   /m/<slug>, so a download shows as an open in the library. */
const AssetsCard = ({ eventId }) => {
  const { t } = useTranslation();
  const [kit, setKit] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    apiRequest('GET', `/mail-plan/${eventId}/assets`).then(setKit).catch((e) => setError(refusal(t, e)));
  }, [eventId, t]);
  if (!kit) return error ? <div className="small text-danger mb-3">{error}</div> : null;
  const files = [...kit.own, ...kit.standard];
  if (!files.length && !kit.builtin.length) return null;
  return (
    <div className="border rounded p-3 mb-3 text-start bg-white">
      <h6>{t('mailPlanAssetsTitle')}</h6>
      <div className="small text-muted mb-2">{t('mailPlanAssetsHint')}</div>
      <ul className="list-unstyled mb-0">
        {kit.builtin.map((b) => (
          <li key={b.url} className="d-flex align-items-center gap-2 mb-1">
            <span className="me-auto">{t(b.kind === 'logo' ? 'mailPlanEventLogo' : 'mailPlanEventBanner')}</span>
            <a className="btn btn-sm btn-outline-secondary" href={b.url} target="_blank" rel="noopener noreferrer">{t('mailPlanDownload')}</a>
          </li>
        ))}
        {files.map((a) => (
          <li key={a.id} className="d-flex align-items-center gap-2 mb-1">
            <span className="me-auto">{a.title}
              {(a.description || a.file_name) && <span className="small text-muted"> · {a.description || a.file_name}</span>}</span>
            <a className="btn btn-sm btn-outline-secondary" href={a.url} target="_blank" rel="noopener noreferrer">
              {t(a.is_link ? 'mailPlanOpen' : 'mailPlanDownload')}
            </a>
          </li>
        ))}
      </ul>
      {error && <div className="small text-danger mt-2">{error}</div>}
    </div>
  );
};

/* "Before we start" — what their side of the plan needs, read off the platform (the delaware
   brief asked for these in a Word form), with a comment thread to answer us or ask. */
const SetupCard = ({ eventId, checklist }) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState([]);
  const [error, setError] = useState(null);
  useEffect(() => {
    apiRequest('GET', `/mail-plan/${eventId}/content`)
      .then((c) => setComments((c.comments && c.comments.setup) || []))
      .catch(() => {});
  }, [eventId]);
  if (!checklist || !checklist.length) return null;
  const send = (text) => apiRequest('POST', `/mail-plan/${eventId}/content/setup/comment`, { text })
    .then((r) => setComments(r.comments || []))
    .catch((e) => setError(refusal(t, e)));
  const mark = (done) => (done === true ? '✔' : done === false ? '●' : 'ℹ');
  const tone = (done) => (done === true ? 'text-success' : done === false ? 'text-warning' : 'text-muted');
  return (
    <div className="border rounded p-3 mb-3 text-start bg-white">
      <h6>{t('mailPlanSetupTitle')}</h6>
      <table className="table table-sm mb-0 small">
        <tbody>
          {checklist.map((c) => (
            <tr key={c.key}>
              <td className={tone(c.done)} style={{ width: 24 }}>{mark(c.done)}</td>
              <td>{t(`mailPlanSetup_${c.key}${c.done === false ? '_missing' : ''}`)}</td>
              <td className="text-muted">{c.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {error && <div className="small text-danger mt-2">{error}</div>}
      <CommentThread comments={comments} onSend={send} />
    </div>
  );
};

/* Who is on each list right now, with their answer — open a list to see the names, take it to a
   spreadsheet with CSV. Their own colleagues. */
const ListsCard = ({ eventId, lists }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(null);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const toggle = (l) => {
    if (open === l.id) { setOpen(null); return; }
    setOpen(l.id);
    setRows(null);
    apiRequest('GET', `/mail-plan/${eventId}/lists/${l.id}`)
      .then((r) => setRows(r.members || []))
      .catch((e) => setError(refusal(t, e)));
  };
  const answer = (a) => t(a === 'going' ? 'mailPlanAnswerGoing' : a === 'declined' ? 'mailPlanAnswerDeclined' : 'mailPlanAnswerNone');
  const csv = (l) => downloadCsv(`${l.name.replace(/[^A-Za-z0-9]+/g, '-').toLowerCase()}.csv`, buildCsv([
    ['Name', 'Email', 'Answer'],
    ...(rows || []).map((m) => [m.name, m.email, answer(m.answer)]),
  ]));
  return (
    <div className="border rounded p-3 mb-5 text-start bg-white">
      <h6>{t('mailPlanListsTitle')}</h6>
      <div className="list-group">
        {lists.map((l) => (
          <React.Fragment key={l.id}>
            <button type="button" className="list-group-item list-group-item-action d-flex" onClick={() => toggle(l)}>
              <span className="me-auto">{l.name}</span><strong>{l.count}</strong>
            </button>
            {open === l.id && (
              <div className="list-group-item small">
                {rows === null ? t('loading') : (
                  <>
                    <div className="d-flex mb-2">
                      <span className="me-auto text-muted">{t('mailPlanListPeople', { n: rows.length })}</span>
                      {rows.length > 0 && (
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => csv(l)}>CSV</button>
                      )}
                    </div>
                    <table className="table table-sm mb-0">
                      <tbody>
                        {rows.slice(0, 200).map((m) => (
                          <tr key={m.id}><td>{m.name || '—'}</td><td className="text-muted">{m.email}</td><td>{answer(m.answer)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                    {rows.length > 200 && <div className="text-muted mt-1">{t('mailPlanListMore', { n: rows.length - 200 })}</div>}
                  </>
                )}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
      {error && <div className="small text-danger mt-2">{error}</div>}
    </div>
  );
};

const SignOffCard = ({ eventId, signOff, onSaved }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(signOff.own ? signOff.name : '');
  const [email, setEmail] = useState(signOff.own ? signOff.email : '');
  const [notice, setNotice] = useState(null);
  const save = (e) => {
    e.preventDefault();
    apiRequest('PUT', `/mail-plan/${eventId}/sign-off`, { name, email })
      .then(() => { setNotice({ kind: 'success', text: t('mailPlanSignOffSaved') }); onSaved(); })
      .catch((err) => setNotice({ kind: 'danger', text: refusal(t, err) }));
  };
  return (
    <div className="border rounded p-3 mb-3 text-start bg-white">
      <h6>{t('mailPlanSignOffTitle')}</h6>
      <div className="small text-muted mb-2">
        {signOff.own ? t('mailPlanSignOffOwn', { name: signOff.name, email: signOff.email || '—' })
          : t('mailPlanSignOffDefault', { name: signOff.name || '—' })}
      </div>
      <form onSubmit={save} className="row g-2">
        <div className="col-md-5"><input className="form-control form-control-sm" maxLength={200} value={name}
               placeholder={t('mailPlanSignOffNamePlaceholder')} onChange={(e) => setName(e.target.value)} /></div>
        <div className="col-md-5"><input className="form-control form-control-sm" type="email" maxLength={200} value={email}
               placeholder={t('mailPlanSignOffEmailPlaceholder')} onChange={(e) => setEmail(e.target.value)} /></div>
        <div className="col-md-2"><button type="submit" className="btn btn-sm btn-outline-secondary w-100">{t('save')}</button></div>
      </form>
      {notice && <div className={`small text-${notice.kind} mt-2`}>{notice.text}</div>}
    </div>
  );
};

const StartListCard = ({ eventId, guestCount, onSaved }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || busy) return;
    const reader = new FileReader();
    reader.onload = () => {
      const rows = parseContactsCsv(reader.result).map((c) => ({ email: c.email, first_name: c.first_name, last_name: c.last_name }));
      if (!rows.length) { setNotice({ kind: 'warning', text: t('contactsNoneFound') }); return; }
      setBusy(true);
      apiRequest('POST', `/mail-plan/${eventId}/guest-list`, { rows })
        .then((r) => { setNotice({ kind: 'success', text: t('mailPlanStartListAdded', { added: r.added, already: r.already }) }); onSaved(); })
        .catch((err) => setNotice({ kind: 'danger', text: refusal(t, err) }))
        .finally(() => setBusy(false));
    };
    reader.readAsText(file);
  };
  return (
    <div className="border rounded p-3 mb-3 text-start bg-white">
      <h6>{t('mailPlanStartListTitle')}</h6>
      <div className="small text-muted mb-2">{t('mailPlanStartListCount', { n: guestCount })} {t('mailPlanStartListHint')}</div>
      <input type="file" accept=".csv,text/csv" className="form-control form-control-sm" disabled={busy} onChange={onFile} />
      {notice && <div className={`small text-${notice.kind} mt-2`}>{notice.text}</div>}
    </div>
  );
};

const EventCommunication = () => {
  const { t, i18n } = useTranslation();
  const { eventId } = useParams();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiRequest('GET', `/mail-plan/${eventId}`)
      .then(setPlan)
      .catch((e) => setError(e.message));
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const approveAll = () => {
    if (!window.confirm(t('mailPlanApproveAllConfirm', { n: plan.to_review }))) return;
    setBusy(true);
    apiRequest('POST', `/mail-plan/${eventId}/approve-all`, {})
      .then(load).catch((e) => setError(e.message)).finally(() => setBusy(false));
  };

  return (
    <Screen color="white">
      <div className="top w-100 px-3">
        <Title>{t('mailPlanTitle')}</Title>
        {error && !plan && <div className="alert alert-warning text-start">{t(error)}</div>}
        {plan && (
          <>
            <div className="small text-muted mb-2">{plan.event.name}</div>
            <p className="text-start small">{t('mailPlanIntro')}</p>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3 text-start">
              <div className="me-auto"><strong>{t('mailPlanProgress', { approved: plan.approved, total: plan.total })}</strong></div>
              {plan.to_review > 0 && (
                <button type="button" className="btn btn-sm btn-success" disabled={busy} onClick={approveAll}>
                  {t('mailPlanApproveAll', { n: plan.to_review })}
                </button>
              )}
            </div>

            <SetupCard eventId={eventId} checklist={plan.checklist} />
            {plan.items.length === 0 && <div className="alert alert-light text-start">{t('mailPlanEmpty')}</div>}
            <div className="list-group mb-4 text-start">
              {plan.items.map((it) => (
                <React.Fragment key={it.id}>
                  <button type="button" className={`list-group-item list-group-item-action d-flex flex-wrap gap-2 align-items-center ${open === it.id ? 'active-subtle' : ''}`}
                          onClick={() => setOpen(open === it.id ? null : it.id)}>
                    <span className="small text-muted" style={{ minWidth: 120 }}>{wallClock(it.when, i18n.language)}</span>
                    <span className="me-auto">
                      <strong>{it.title}</strong>
                      <span className="d-block small text-muted">{it.subject}</span>
                    </span>
                    <span className="small text-muted">{it.list ? `${it.list} · ${it.count ?? ''}` : ''}</span>
                    {it.open_comments > 0 && <span className="badge bg-warning text-dark">{it.open_comments} 💬</span>}
                    <span className={`badge ${STATUS_CLASS[it.status] || 'bg-light text-dark'}`}>
                      {t(`mailPlanStatus_${it.status}`)}{it.status === 'sent' && it.sent_count != null ? ` · ${it.sent_count}` : ''}
                    </span>
                  </button>
                  {open === it.id && (
                    <div className="list-group-item p-2 bg-light">
                      <MailDetail eventId={eventId} itemId={it.id} onChanged={load} />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            <ContentCard eventId={eventId} />
            <AutomaticMailsCard eventId={eventId} />
            <AssetsCard eventId={eventId} />
            <SignOffCard eventId={eventId} signOff={plan.sign_off} onSaved={load} />
            <StartListCard eventId={eventId} guestCount={plan.guest_count} onSaved={load} />
            {plan.lists.length > 0 && <ListsCard eventId={eventId} lists={plan.lists} />}
          </>
        )}
      </div>
    </Screen>
  );
};

export default EventCommunication;
